/**
 * Maintenance Requests — tenant/landlord unified UI for the new
 * /api/maint endpoints. Tenants can create requests + upload
 * photos/videos + propose availability slots. Landlords confirm a
 * slot + move status along.
 */
import { useEffect, useState, useCallback } from "react";
import { PageHeader, Card, Button, Pill, Loader, Input, Textarea, Modal, EmptyState } from "@/components/ui/Primitives";
import { Camera, Video as VideoIcon, Plus, Clock, CheckCircle2, Send, Calendar, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}` });

const STATUSES = ["open", "acknowledged", "scheduled", "in_progress", "completed", "cancelled"];
const STATUS_TONE = { open: "amber", acknowledged: "blue", scheduled: "blue", in_progress: "blue", completed: "green", cancelled: "slate" };

export default function MaintenanceRequests() {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/maint/requests`, { headers: auth() });
      const d = await r.json();
      setItems(d.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader/>;

  return (
    <div data-testid="maintenance-page">
      <PageHeader
        title="Maintenance requests"
        subtitle={role === "tenant" ? "Report an issue with photos, videos and your availability." : "Manage tenant maintenance requests, review media, confirm visit times."}
      />
      {role === "tenant" && (
        <div className="mb-4 flex justify-end">
          <Button onClick={() => setCreateOpen(true)} data-testid="maint-new-btn"><Plus className="w-4 h-4 inline mr-1"/>New request</Button>
        </div>
      )}
      {items.length === 0 ? (
        <EmptyState icon={Clock} title="No requests yet" description={role === "tenant" ? "Report your first maintenance issue — a leaky faucet, broken heater, whatever needs fixing." : "Your tenants haven't submitted any maintenance requests yet."} />
      ) : (
        <div className="grid gap-3">
          {items.map((r) => (
            <Card key={r.id} className="p-4 cursor-pointer hover:border-emerald-300" onClick={() => setActive(r)} data-testid={`maint-item-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 truncate">{r.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{new Date(r.created_at).toLocaleString()} · {r.priority}</div>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{r.description}</p>
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1">
                  <Pill tone={STATUS_TONE[r.status] || "slate"}>{r.status.replace(/_/g, " ")}</Pill>
                  {r.media?.length > 0 && <div className="text-[11px] text-slate-500">{r.media.length} media</div>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {createOpen && <NewRequestModal onClose={() => setCreateOpen(false)} onCreated={(m) => { setCreateOpen(false); setItems((s) => [m, ...s]); setActive(m); }}/>}
      {active && <RequestDetail req={active} role={role} onClose={() => { setActive(null); load(); }} onUpdated={(m) => setActive(m)}/>}
    </div>
  );
}

function NewRequestModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", description: "", priority: "normal" });
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/maint/requests`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not create");
      toast.success("Request submitted");
      onCreated(d);
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }
  return (
    <Modal open={true} onClose={onClose} title="New maintenance request" testid="maint-new-modal">
      <form onSubmit={submit} className="space-y-4">
        <Input label="Title" required placeholder="Leaky bathroom faucet" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} testid="maint-title"/>
        <Textarea label="Describe the issue" required placeholder="What's happening, when it started, and anything relevant." value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} testid="maint-desc"/>
        <div>
          <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Priority</label>
          <select value={form.priority} onChange={(e)=>setForm({...form,priority:e.target.value})} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="maint-priority">
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="emergency">Emergency</option>
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy} data-testid="maint-submit">{busy ? "Submitting…" : "Submit request"}</Button>
        </div>
      </form>
    </Modal>
  );
}

function RequestDetail({ req, role, onClose, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [slotsForm, setSlotsForm] = useState("");
  const [confirmSlot, setConfirmSlot] = useState("");
  const [runbook, setRunbook] = useState(null);
  const [runbookBusy, setRunbookBusy] = useState(false);

  async function triage(auto = false) {
    setRunbookBusy(true);
    try {
      const path = auto ? "dispatch" : "triage";
      const r = await fetch(`${API}/api/ai-runbook/${path}/${req.id}`, {
        method: "POST", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ auto }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Runbook failed");
      setRunbook(d);
      if (auto && d.dispatched) {
        toast.success(`Mark dispatched ${d.vendor?.name}`);
        onUpdated({ ...req, vendor_assigned: d.vendor, status: "acknowledged" });
      } else {
        toast.success("Runbook ready — review below.");
      }
    } catch (err) { toast.error(String(err.message)); }
    finally { setRunbookBusy(false); }
  }

  async function upload(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 100 * 1024 * 1024) return toast.error("File exceeds 100 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await fetch(`${API}/api/maint/requests/${req.id}/upload`, {
        method: "POST",
        headers: { ...auth() },
        body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Upload failed");
      const upd = { ...req, media: [...(req.media || []), d] };
      onUpdated(upd);
      toast.success(`Uploaded ${d.kind}`);
    } catch (err) { toast.error(String(err.message)); }
    finally { setUploading(false); }
  }

  async function setStatus(status) {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/maint/requests/${req.id}/status`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Update failed");
      onUpdated(d);
      toast.success(`Status → ${status.replace(/_/g, " ")}`);
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }

  async function proposeSlots() {
    const slots = slotsForm.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!slots.length) return toast.error("Enter at least one date/time");
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/maint/requests/${req.id}/schedule`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ slots }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Schedule failed");
      onUpdated(d);
      setSlotsForm("");
      toast.success("Availability sent to landlord");
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }

  async function confirm() {
    if (!confirmSlot) return toast.error("Pick a slot");
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/maint/requests/${req.id}/confirm`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ confirmed_slot: confirmSlot }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Confirm failed");
      onUpdated(d);
      toast.success("Slot confirmed");
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }

  return (
    <Modal open={true} onClose={onClose} title={req.title} testid="maint-detail-modal">
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Pill tone={STATUS_TONE[req.status] || "slate"}>{req.status.replace(/_/g, " ")}</Pill>
          <Pill tone="slate">{req.priority}</Pill>
          {req.confirmed_slot && <Pill tone="green">Scheduled: {req.confirmed_slot}</Pill>}
        </div>
        <p className="text-sm text-slate-700 whitespace-pre-wrap">{req.description}</p>

        {/* MEDIA */}
        <div>
          <h4 className="text-xs font-bold uppercase text-slate-500 tracking-wider mb-2">Media</h4>
          {req.media?.length === 0 && <div className="text-xs text-slate-400 italic">No media yet.</div>}
          <div className="grid grid-cols-3 gap-2">
            {req.media?.map((m, i) => (
              <a key={i} href={`${API}${m.url}`} target="_blank" rel="noreferrer" className="block relative aspect-square rounded-lg overflow-hidden border border-slate-200" data-testid={`maint-media-${i}`}>
                {m.kind === "video" ? (
                  <div className="w-full h-full bg-slate-900 text-white flex items-center justify-center"><VideoIcon className="w-6 h-6"/></div>
                ) : (
                  <img src={`${API}${m.url}`} alt="" className="w-full h-full object-cover"/>
                )}
              </a>
            ))}
          </div>
          <label className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 hover:text-emerald-900 cursor-pointer" data-testid="maint-upload-label">
            <Camera className="w-3.5 h-3.5"/> {uploading ? "Uploading…" : "Add photo / video (max 100 MB)"}
            <input type="file" accept="image/*,video/*" className="hidden" onChange={upload} data-testid="maint-upload-input"/>
          </label>
        </div>

        {/* SCHEDULING */}
        {role === "tenant" && !req.confirmed_slot && (
          <div>
            <h4 className="text-xs font-bold uppercase text-slate-500 tracking-wider mb-2 flex items-center gap-1"><Calendar className="w-3 h-3"/> Your availability</h4>
            <Textarea label="One slot per line" placeholder={"Sat Feb 10, 2–4 pm\nSun Feb 11, 9–11 am"} value={slotsForm} onChange={(e) => setSlotsForm(e.target.value)} testid="maint-slots-input"/>
            <Button onClick={proposeSlots} disabled={busy} className="mt-2 !py-1.5 !px-3 text-xs" data-testid="maint-slots-submit"><Send className="w-3 h-3 inline mr-1"/>Send availability</Button>
            {req.slots_proposed?.length > 0 && (
              <ul className="mt-2 text-xs text-slate-600 list-disc list-inside">
                {req.slots_proposed.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            )}
          </div>
        )}
        {role === "landlord" && req.slots_proposed?.length > 0 && !req.confirmed_slot && (
          <div>
            <h4 className="text-xs font-bold uppercase text-slate-500 tracking-wider mb-2">Tenant availability</h4>
            <div className="space-y-1">
              {req.slots_proposed.map((s, i) => (
                <label key={i} className="flex items-center gap-2 text-sm">
                  <input type="radio" name="slot" value={s} checked={confirmSlot === s} onChange={() => setConfirmSlot(s)} data-testid={`maint-slot-radio-${i}`}/>
                  {s}
                </label>
              ))}
            </div>
            <Button onClick={confirm} disabled={busy || !confirmSlot} className="mt-2 !py-1.5 !px-3 text-xs" data-testid="maint-slot-confirm"><CheckCircle2 className="w-3 h-3 inline mr-1"/>Confirm slot</Button>
          </div>
        )}

        {/* AI RUNBOOK — landlord only */}
        {role === "landlord" && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-bold text-emerald-900 flex items-center gap-1.5"><Sparkles className="w-4 h-4"/> Mark's runbook</div>
                <div className="text-[11px] text-emerald-800">One click: classify, pick a vendor, draft messages, dispatch.</div>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <Button variant="outline" onClick={() => triage(false)} disabled={runbookBusy} className="!py-1.5 !px-3 text-xs" data-testid="maint-runbook-triage-btn">
                  {runbookBusy ? "Thinking…" : "Preview"}
                </Button>
                <Button onClick={() => triage(true)} disabled={runbookBusy} className="!py-1.5 !px-3 text-xs" data-testid="maint-runbook-dispatch-btn">
                  <Zap className="w-3 h-3 inline mr-1"/>Dispatch
                </Button>
              </div>
            </div>
            {runbook && (
              <div className="mt-3 text-xs text-slate-700 space-y-1.5" data-testid="maint-runbook-result">
                <div><span className="font-bold">Category:</span> {runbook.category} · <span className="font-bold">Urgency:</span> {runbook.urgency} · <span className="text-slate-500">({runbook.source})</span></div>
                {runbook.vendor && (
                  <div><span className="font-bold">Suggested vendor:</span> {runbook.vendor.name} · {runbook.vendor.phone || "no phone"} {runbook.vendor.preferred && <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold text-[10px]">PREFERRED</span>}</div>
                )}
                {!runbook.vendor && <div className="text-amber-700">No vendor found. Add one to your rolodex or ping a public vendor.</div>}
                {runbook.drafts && (
                  <details className="mt-1">
                    <summary className="cursor-pointer font-semibold text-emerald-800">Message drafts</summary>
                    <div className="mt-2 space-y-2">
                      <div><div className="font-bold text-[10px] uppercase">To vendor</div><pre className="whitespace-pre-wrap text-[11px] bg-white/70 p-2 rounded border border-emerald-200">{runbook.drafts.vendor_message}</pre></div>
                      <div><div className="font-bold text-[10px] uppercase">To tenant</div><pre className="whitespace-pre-wrap text-[11px] bg-white/70 p-2 rounded border border-emerald-200">{runbook.drafts.tenant_message}</pre></div>
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        )}

        {/* STATUS ACTIONS */}
        <div className="flex flex-wrap gap-2">
          {STATUSES.filter((s) => s !== req.status).map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              disabled={busy}
              className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700"
              data-testid={`maint-status-${s}`}
            >
              → {s.replace(/_/g, " ")}
            </button>
          ))}
        </div>

        {/* TIMELINE */}
        <div>
          <h4 className="text-xs font-bold uppercase text-slate-500 tracking-wider mb-2">Timeline</h4>
          <ol className="space-y-1.5">
            {(req.timeline || []).slice().reverse().map((t, i) => (
              <li key={i} className="text-xs text-slate-600 flex gap-2">
                <span className="text-slate-400 shrink-0">{new Date(t.at).toLocaleString()}</span>
                <span className="font-semibold text-slate-700 shrink-0">[{t.role}]</span>
                <span>{t.note}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </Modal>
  );
}
